import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "utils/api";
import { AlertsContext } from "providers/alerts";
import { useContext } from "react";
import { Tables } from "types/supabase";

export const getLendersQueryKey = () => ["lenders"];

type Lender = Tables<"users"> & {
  role: Tables<"roles">;
};

const fetchLenders = async (): Promise<Lender[]> => {
  const { users }: { users: Lender[] } = await apiRequest("/supabase/lenders/select");

  // Sorting the borrowers by name
  return users.sort((a, b) => {
    const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
    const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });
};

export const useLenders = ({ enabled = true }: { enabled?: boolean }) => {
  const { showError } = useContext(AlertsContext);

  const {
    data: lenders = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery<Lender[], Error>({
    queryKey: getLendersQueryKey(),
    queryFn: fetchLenders,
    gcTime: 5 * 60 * 1000, // 5 minutes
    staleTime: 60 * 1000, // 1 minute
    enabled,
  });

  // Handle errors separately since React Query v5 doesn't have onError in options
  if (lenders === null) {
    showError({
      title: "Lenders Not Loaded",
      description: "Failed to load lenders",
    });
  }

  return {
    loading: isLoading || isFetching,
    lenders,
    fetchLenders: refetch,
  };
};
